import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  ExpandLess,
  ExpandMore,
  LocalHospital as NurseIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Sick as PatientIcon,
  Timeline as ActivityIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { AppUpdateNotifier, AppVersionDisplay } from './common';

const drawerWidth = 240;
const COLORS = {
  // 背景色
  selectedBackground: 'rgba(25, 118, 210, 0.12)',
  selectedBackgroundHover: 'rgba(25, 118, 210, 0.20)',
  sectionBackground: 'rgba(25, 118, 210, 0.08)',
  submenuBackground: 'rgba(25, 118, 210, 0.04)',
  // テキスト色
  sectionHeader: 'text.secondary',
  primary: '#1976d2', // プライマリーカラー
  // ボーダー色
  divider: '#e0e0e0',
};

interface LayoutProps {
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [ mobileOpen, setMobileOpen ] = useState(false);
  const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>(null);
  const [ settingsOpen, setSettingsOpen ] = useState(true); // デフォルトで開いている状態に
  const [ activityOpen, setActivityOpen ] = useState(true); // デフォルトで開いている状態に
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isDeveloper } = useAuth();
  // 現在のパスを取得
  const currentPath = location.pathname;
  // ページロード時に現在のパスがセクション内ならメニューを開いておく
  useEffect(() => {
    if (currentPath.startsWith('/settings/')) {
      setSettingsOpen(true);
    }
    if (currentPath.startsWith('/activity/') || currentPath === '/users') {
      setActivityOpen(true);
    }
  }, [ currentPath ]);
  // 設定メニューの展開/折りたたみを切り替える
  const handleSettingsClick = () => {
    setSettingsOpen(!settingsOpen);
  };
  // アクティビティメニューの展開/折りたたみを切り替える
  const handleActivityClick = () => {
    setActivityOpen(!activityOpen);
  };
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };
  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Tomopiia Admin
        </Typography>
      </Toolbar>
      <Divider/>
      <List>
        {/* 標準メニュー */}
        <ListItemButton
          component={Link}
          to="/dashboard"
          selected={currentPath === '/dashboard'}
          sx={{
            '&.Mui-selected': {
              backgroundColor: COLORS.selectedBackground,
              '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
            }
          }}
        >
          <ListItemIcon>
            <DashboardIcon color={currentPath === '/dashboard' ? 'primary' : undefined}/>
          </ListItemIcon>
          <ListItemText primary="ダッシュボード" primaryTypographyProps={{
            fontWeight: currentPath === '/dashboard' ? 'bold' : undefined
          }}/>
        </ListItemButton>
        <ListItemButton
          component={Link}
          to="/nurses"
          selected={currentPath === '/nurses' || currentPath.startsWith('/nurses/')}
          sx={{
            '&.Mui-selected': {
              backgroundColor: COLORS.selectedBackground,
              '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
            }
          }}
        >
          <ListItemIcon>
            <NurseIcon color={currentPath === '/nurses' || currentPath.startsWith('/nurses/') ? 'primary' : undefined}/>
          </ListItemIcon>
          <ListItemText primary="看護師" primaryTypographyProps={{
            fontWeight: currentPath === '/nurses' || currentPath.startsWith('/nurses/') ? 'bold' : undefined
          }}/>
        </ListItemButton>

        {/* 患者メニュー */}
        <ListItemButton
          component={Link}
          to="/patients"
          selected={currentPath === '/patients' || currentPath.startsWith('/patients/')}
          sx={{
            '&.Mui-selected': {
              backgroundColor: COLORS.selectedBackground,
              '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
            }
          }}
        >
          <ListItemIcon>
            <PatientIcon color={currentPath === '/patients' || currentPath.startsWith('/patients/') ? 'primary' : undefined}/>
          </ListItemIcon>
          <ListItemText primary="患者" primaryTypographyProps={{
            fontWeight: currentPath === '/patients' || currentPath.startsWith('/patients/') ? 'bold' : undefined
          }}/>
        </ListItemButton>
        <ListItemButton
          component={Link}
          to="/talk_rooms"
          selected={currentPath === '/talk_rooms' || currentPath.startsWith('/talk_rooms/')}
          sx={{
            '&.Mui-selected': {
              backgroundColor: COLORS.selectedBackground,
              '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
            }
          }}
        >
          <ListItemIcon>
            <ChatIcon color={currentPath === '/talk_rooms' || currentPath.startsWith('/talk_rooms/') ? 'primary' : undefined}/>
          </ListItemIcon>
          <ListItemText primary="対話" primaryTypographyProps={{
            fontWeight: currentPath === '/talk_rooms' || currentPath.startsWith('/talk_rooms/') ? 'bold' : undefined
          }}/>
        </ListItemButton>

        {/* アクティビティメニュー */}
        <Divider sx={{ my: 2 }}/>
        <ListItemButton onClick={handleActivityClick}>
          <ListItemIcon>
            <ActivityIcon color={currentPath.startsWith('/activity/') ? 'primary' : undefined}/>
          </ListItemIcon>
          <ListItemText
            primary="アクティビティ"
            primaryTypographyProps={{
              fontWeight: currentPath.startsWith('/activity/') ? 'bold' : undefined,
            }}
          />
          {activityOpen ? <ExpandLess/> : <ExpandMore/>}
        </ListItemButton>

        {/* アクティビティサブメニュー - 折りたたみ可能 */}
        <Box
          sx={{
            overflow: 'hidden',
            transition: 'max-height 0.3s ease-in-out',
            maxHeight: activityOpen ? '500px' : '0px', // 閉じた時は高さ0、開いた時は十分な高さ（高さを増やす）
            backgroundColor: COLORS.submenuBackground
          }}
        >
          <ListItemButton
            component={Link}
            to="/activity/chat-message-deletion-history"
            sx={{
              pl: 4, // 親メニューよりインデント
              '&.Mui-selected': {
                backgroundColor: COLORS.selectedBackground,
                '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
              }
            }}
            selected={currentPath === '/activity/chat-message-deletion-history' || currentPath.startsWith('/activity/chat-message-deletion-history/')}
          >
            <ListItemText
              primary="チャット削除履歴"
              primaryTypographyProps={{
                fontWeight: currentPath === '/activity/chat-message-deletion-history' || currentPath.startsWith('/activity/chat-message-deletion-history/') ? 'bold' : undefined
              }}
            />
          </ListItemButton>

          {/* 仮登録アカウント */}
          <ListItemButton
            component={Link}
            to="/users"
            sx={{
              pl: 4, // 親メニューよりインデント
              '&.Mui-selected': {
                backgroundColor: COLORS.selectedBackground,
                '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
              }
            }}
            selected={currentPath === '/users' || currentPath.startsWith('/users/')}
          >
            <ListItemText
              primary="仮登録アカウント"
              primaryTypographyProps={{
                fontWeight: currentPath === '/users' || currentPath.startsWith('/users/') ? 'bold' : undefined
              }}
            />
          </ListItemButton>
        </Box>

        {/* 設定メニュー */}
        <Divider sx={{ my: 2 }}/>
        <ListItemButton onClick={handleSettingsClick}>
          <ListItemIcon>
            <SettingsIcon color={currentPath.startsWith('/settings/') ? 'primary' : undefined}/>
          </ListItemIcon>
          <ListItemText
            primary="設定"
            primaryTypographyProps={{
              fontWeight: currentPath.startsWith('/settings/') ? 'bold' : undefined,
            }}
          />
          {settingsOpen ? <ExpandLess/> : <ExpandMore/>}
        </ListItemButton>

        {/* 設定サブメニュー - 折りたたみ可能 */}
        <Box
          sx={{
            overflow: 'hidden',
            transition: 'max-height 0.3s ease-in-out',
            maxHeight: settingsOpen ? '300px' : '0px', // 高さを増やして新しいメニュー項目に対応
            backgroundColor: COLORS.submenuBackground
          }}
        >
          <ListItemButton
            component={Link}
            to="/settings/certification-codes"
            sx={{
              pl: 4, // 親メニューよりインデント
              '&.Mui-selected': {
                backgroundColor: COLORS.selectedBackground,
                '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
              }
            }}
            selected={currentPath === '/settings/certification-codes' || currentPath.startsWith('/settings/certification-codes/')}
          >
            <ListItemText
              primary="看護師認証コード"
              primaryTypographyProps={{
                fontWeight: currentPath === '/settings/certification-codes' || currentPath.startsWith('/settings/certification-codes/') ? 'bold' : undefined
              }}
            />
          </ListItemButton>

          <ListItemButton
            component={Link}
            to="/coupons"
            sx={{
              pl: 4, // 親メニューよりインデント
              '&.Mui-selected': {
                backgroundColor: COLORS.selectedBackground,
                '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
              }
            }}
            selected={currentPath === '/coupons' || currentPath.startsWith('/coupons/')}
          >
            <ListItemText
              primary="クーポン"
              primaryTypographyProps={{
                fontWeight: currentPath === '/coupons' || currentPath.startsWith('/coupons/') ? 'bold' : undefined
              }}
            />
          </ListItemButton>

          <ListItemButton
            component={Link}
            to="/talk_plan_masters"
            sx={{
              pl: 4, // 親メニューよりインデント
              '&.Mui-selected': {
                backgroundColor: COLORS.selectedBackground,
                '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
              }
            }}
            selected={currentPath === '/talk_plan_masters' || currentPath.startsWith('/talk_plan_masters/')}
          >
            <ListItemText
              primary="対話プラン"
              primaryTypographyProps={{
                fontWeight: currentPath === '/talk_plan_masters' || currentPath.startsWith('/talk_plan_masters/') ? 'bold' : undefined
              }}
            />
          </ListItemButton>
        </Box>

        {/* 管理者メニュー */}
        {user && user.role === 'ADMIN' && (
          <>
            <Divider sx={{ my: 2 }}/>
            <ListSubheader component="div" sx={{ backgroundColor: COLORS.sectionBackground, color: COLORS.sectionHeader, fontWeight: 500 }}>
              管理者メニュー
            </ListSubheader>
            <ListItemButton
              component={Link}
              to="/manage/users"
              sx={{
                '&.Mui-selected': {
                  backgroundColor: COLORS.selectedBackground,
                  '&:hover': { backgroundColor: COLORS.selectedBackgroundHover }
                }
              }}
              selected={currentPath === '/manage/users' || currentPath.startsWith('/manage/users/')}
            >
              <ListItemIcon>
                <AdminIcon color={currentPath === '/manage/users' || currentPath.startsWith('/manage/users/') ? 'primary' : undefined}/>
              </ListItemIcon>
              <ListItemText
                primary="ユーザー管理"
                primaryTypographyProps={{
                  fontWeight: currentPath === '/manage/users' || currentPath.startsWith('/manage/users/') ? 'bold' : undefined
                }}
              />
            </ListItemButton>
            {/* 他の管理者機能をここに追加 */}
          </>
        )}
      </List>
    </div>
  );
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline/>
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` }, }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon/>
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {currentPath.includes('/talk_rooms') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                対話
              </Typography>
            )}
            {currentPath.includes('/nurses') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                看護師
              </Typography>
            )}
            {currentPath.includes('/patients') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                患者
              </Typography>
            )}
            {currentPath.includes('/manage/users') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                ユーザー管理
              </Typography>
            )}
            {currentPath.includes('/dashboard') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                ダッシュボード
              </Typography>
            )}
            {currentPath.includes('/settings/certification-codes') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                看護師認証コード
              </Typography>
            )}
            {currentPath.includes('/activity/chat-message-deletion-history') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                チャット削除履歴
              </Typography>
            )}
            {currentPath.includes('/users') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                仮登録アカウント
              </Typography>
            )}
            {currentPath.includes('/coupons') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                クーポン管理
              </Typography>
            )}
            {currentPath.includes('/talk_plan_masters') && (
              <Typography variant="h5" component="div" sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.4rem' }}>
                対話プラン
              </Typography>
            )}
          </Box>

          {/* ログインユーザー名の表示 */}
          {user && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
              <Typography variant="body2" sx={{ color: 'white' }}>
                {user.last_name} {user.first_name}
                {user.is_superuser && ' (システム管理者)'}
                {!user.is_superuser && user.role === 'ADMIN' && ' (管理者)'}
              </Typography>
              {isDeveloper() && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.65rem',
                    letterSpacing: '0.02em',
                    lineHeight: 1
                  }}
                >
                  Developer Mode
                </Typography>
              )}
            </Box>
          )}

          <IconButton
            onClick={handleMenuOpen}
            sx={{ p: 0 }}
            aria-controls="menu-appbar"
            aria-haspopup="true"
          >
            <Avatar alt={user ? `${user.last_name} ${user.first_name}` : 'User'}/>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => {
              handleMenuClose();
              navigate('/profile');
            }}>プロフィール</MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small"/>
              </ListItemIcon>
              ログアウト
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="mailbox folders">
        {/* モバイル用ドロワー */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // モバイルでのパフォーマンス向上
          }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }, }}
        >
          {drawer}
        </Drawer>
        {/* デスクトップ用ドロワー */}
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }, }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh'
        }}
      >
        <Toolbar/>
        <Box sx={{ flexGrow: 1 }}>
          {children || <Outlet/>}
        </Box>
        <Box component="footer" sx={{ mt: 'auto', py: 2, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">
            &copy; 2025 Tomopiia Admin. All rights reserved.
          </Typography>
          <AppVersionDisplay/>
        </Box>
        <AppUpdateNotifier/>
      </Box>
    </Box>
  );
};
export default Layout;
